import React from 'react';

import { type FieldPath, useFormContext } from 'react-hook-form';

import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';

type PriceFormFieldProps<TFormValues extends Record<string, any>> = {
    value: string | number | undefined;
    fieldName: FieldPath<TFormValues>;
    label: string;
    disabled?: boolean;
    isRequired?: boolean;
    setDisplayedFormValues?: React.Dispatch<React.SetStateAction<Partial<TFormValues>>>;
};

function PriceFormField<TFormValues extends Record<string, any>>({
    value,
    fieldName,
    label,
    disabled,
    isRequired,
    setDisplayedFormValues,
}: PriceFormFieldProps<TFormValues>) {
    const { control } = useFormContext<TFormValues>();

    const handleChange = React.useCallback(
        (onChange: (...event: any[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value;
            const isValid = /^\d*(\.?\d{0,2})?$/.test(inputValue);
            if (!isValid && inputValue !== '') return;

            if (setDisplayedFormValues) {
                setDisplayedFormValues((prev) => ({ ...prev, [fieldName]: inputValue }));
            }

            const parsed = parseFloat(inputValue);

            if (fieldName === 'tax_percent') {
                if (!isNaN(parsed) && parsed > 100) return;
                const decimal = !isNaN(parsed) ? (parsed / 100).toFixed(4) : '';
                onChange(decimal);
            } else {
                if (!isNaN(parsed)) {
                    onChange(Math.round(parsed * 100));
                } else {
                    onChange(0);
                }
            }
        },
        [],
    );

    return (
        <FormField
            control={control}
            name={fieldName}
            render={({ field }) => (
                <FormItem variant="dialog">
                    <FormLabel variant="dialog" required={isRequired}>
                        {label}
                    </FormLabel>
                    <FormControlStack>
                        <FormControl>
                            <Input
                                inputMode="decimal"
                                type="text"
                                pattern="^\d*(\.?\d{0,2})?$"
                                value={value ?? '0.00'}
                                placeholder="0.00"
                                disabled={disabled}
                                onChange={handleChange(field.onChange)}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormControlStack>
                </FormItem>
            )}
        />
    );
}

export default PriceFormField;
